// src/services/api.js

import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  DEFAULT_API_BASE_URL, 
  API_TIMEOUT, 
  AUTH_TOKEN_KEY, 
  USER_DATA_KEY, 
  HTTP_UNAUTHORIZED 
} from '../constants';


const API_BASE_URL = process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL;
export const SERVER_BASE_URL = process.env.REACT_APP_SERVER_URL || API_BASE_URL.replace('/api', '');
export { API_BASE_URL };

// Helper function para obtener headers con autenticación
export const getAuthHeaders = () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};


// Configurar axios

const api = axios.create({

  baseURL: API_BASE_URL,

  timeout: API_TIMEOUT,

  headers: {

    'Content-Type': 'application/json'

  }

});


// Interceptor para requests

api.interceptors.request.use(

  (config) => {

    console.log(`������ ${config.method?.toUpperCase()} ${config.url}`);

    // Agregar token de autorización si existe
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Agregar headers de tenant context automáticamente desde la URL
    // Solo para endpoints específicos que requieren contexto de tenant
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      // Detectar si estamos en una ruta de biller: /billers/{billerId}/...
      const billerMatch = currentPath.match(/\/billers\/([^\/]+)/);

      if (billerMatch) {
        const billerId = decodeURIComponent(billerMatch[1]);

        // Lista de endpoints que NO deben recibir headers de tenant
        const excludedEndpoints = [
          '/payments/hierarchy/publishers',
          '/payments/hierarchy/billers',
          '/admin/publishers',
          '/admin/tenants',
          '/admin/users',
          '/permissions/',
          '/users/',
          '/auth/'
        ];

        // Verificar si la URL actual está en la lista de exclusión
        const shouldExclude = excludedEndpoints.some(endpoint =>
          config.url && config.url.includes(endpoint)
        );

        if (!shouldExclude) {
          // Obtener publisherId desde localStorage (se guarda cuando el usuario se loguea)
          const userData = localStorage.getItem(USER_DATA_KEY);
          if (userData) {
            try {
              const user = JSON.parse(userData);
              const publisherId = user.assigned_publisher_id || user.publisher_id;

              if (publisherId && billerId) {
                // Solo agregar si no están ya presentes en los headers
                if (!config.headers['X-Publisher-ID']) {
                  config.headers['X-Publisher-ID'] = publisherId;
                }
                if (!config.headers['X-Biller-ID']) {
                  config.headers['X-Biller-ID'] = billerId;
                }
              }
            } catch (e) {
              // Ignorar errores de parsing
            }
          }
        }
      }
    }

    return config;

  },

  (error) => {

    console.error('❌ Error en request:', error);

    return Promise.reject(error);

  }

);


// Interceptor para responses
// Note: Global 401 handling is now done in axiosConfig.js via setupAxiosInterceptors
// This interceptor only handles logging and non-401 errors

api.interceptors.response.use(

  (response) => {

    console.log(`✅ ${response.status} ${response.config.url}`);

    return response;

  },

  (error) => {

    console.error('❌ Error en response:', error);

    // 401 errors are handled globally by axiosConfig.js interceptor
    // Don't handle them here to avoid duplicate redirects
    if (error.response?.status === HTTP_UNAUTHORIZED) {
      // Let the global interceptor handle this
      return Promise.reject(error);
    }

    // Mostrar error al usuario (solo para errores no-401)

    const message = error.response?.data?.message || error.message || 'Error de conexión';

    toast.error(message);



    return Promise.reject(error);

  }

);


// API de pagos

export const paymentsAPI = {

  // Obtener todos los pagos

  getPayments: (params = {}) => {

    return api.get('/payments', { params });

  },


  // Obtener un pago específico

  getPayment: (paymentReference, publisherId, billerId) => {
    // Si se proporcionan publisherId y billerId, usar el endpoint multi-tenant
    if (publisherId && billerId) {
      return api.get(`/payments/${paymentReference}`, {
        headers: {
          'X-Publisher-ID': publisherId,
          'X-Biller-ID': billerId
        }
      });
    }

    // Fallback para compatibilidad
    return api.get(`/payments/${paymentReference}`);
  },


  // Crear nuevo pago

  createPayment: (data) => {

    return api.post('/payments', data);

  },


  // Marcar pago como completado

  markAsPaid: (paymentReference, data) => {

    return api.patch(`/payments/${paymentReference}/mark-paid`, data);

  },


  // Enviar notificación manual

  sendNotification: (paymentReference, templateType) => {

    return api.post(`/payments/${paymentReference}/send-notification`, { templateType });

  },

  // === NUEVAS FUNCIONES PARA LAZY LOADING ===

  // Obtener solo Publishers con totales
  getPublishers: () => {
    return api.get('/payments/hierarchy/publishers');
  },

  // Obtener Billers de un Publisher específico
  getBillersByPublisher: (publisherId) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers`);
  },

  // Obtener SOLO los lotes de un Publisher-Biller específico (sin pagos)
  getBatchesByPublisherBiller: (publisherId, billerId) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/batches`);
  },

  // Obtener pagos de un Publisher-Biller específico con paginación
  getPaymentsByPublisherBiller: (publisherId, billerId, params = {}) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/payments`, { params });
  },

  // Obtener información detallada del lote para eliminación
  getBatchInfo: (batchId) => {
    return api.get(`/payments/batch/${encodeURIComponent(batchId)}/info`);
  },


  // Actualizar fecha de vencimiento
  updateDueDate: (paymentReference, dueDate) => {
    return api.patch(`/payments/${paymentReference}/due-date`, { due_date: dueDate });
  },

  // Obtener datos públicos de un pago (sin autenticación)
  getPublicPayment: (paymentReference) => {
    return api.get(`/public/payments/${paymentReference}`);
  },

  // Marcar pago como pagado desde página pública
  markAsPublicPaid: (paymentReference, data = {}) => {
    return api.patch(`/public/payments/${paymentReference}/mark-paid`, data);
  },

  // === NUEVAS FUNCIONES PARA PAGOS PARCIALES ===

  // Crear pago parcial público
  createPublicPartialPayment: (paymentReference, data) => {
    return api.post(`/public/payments/${paymentReference}/pay-partial`, data);
  },

  // Obtener historial de transacciones público
  getPublicPaymentTransactions: (paymentReference) => {
    return api.get(`/public/payments/${paymentReference}/transactions`);
  },

  // Obtener balance de pago público
  getPublicPaymentBalance: (paymentReference) => {
    return api.get(`/public/payments/${paymentReference}/balance`);
  },

  // Crear pago parcial (autenticado)
  createPartialPayment: (paymentReference, data) => {
    return api.post(`/payments/${paymentReference}/transactions`, data);
  },

  // Obtener transacciones de pago (autenticado)
  getPaymentTransactions: (paymentReference) => {
    return api.get(`/payments/${paymentReference}/transactions`);
  },

  // Obtener balance de pago (autenticado)
  getPaymentBalance: (paymentReference) => {
    return api.get(`/payments/${paymentReference}/balance`);
  },

  // Validar pago parcial
  validatePartialPayment: (paymentReference, data) => {
    return api.post(`/payments/${paymentReference}/validate-partial`, data);
  },

  // Reversar transacción
  reverseTransaction: (transactionId, data) => {
    return api.put(`/payments/transactions/${transactionId}/reverse`, data);
  },

  // Obtener estadísticas de pagos parciales
  getPartialPaymentsStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/payments/stats/partial-payments${queryString ? '?' + queryString : ''}`);
  }

};


// API de configuración

export const configAPI = {

  // Obtener configuración de recordatorios

  getReminderConfig: () => {

    return api.get('/payments/config/reminders');

  },


  // Actualizar configuración

  updateReminderConfig: (id, data) => {

    return api.put(`/payments/config/reminders/${id}`, data);

  }

};


// API de estadísticas

export const statsAPI = {

  // Obtener estadísticas del dashboard

  getDashboardStats: () => {

    return api.get('/payments/dashboard/stats');

  },


  // Obtener historial de notificaciones

  getNotificationHistory: (params = {}) => {

    return api.get('/payments/notifications/history', { params });

  },


  // Obtener estadísticas de notificaciones
  getNotificationStats: (params = {}) => {
    return api.get('/payments/notifications/stats', { params });
  },

  // Eliminar notificación individual
  deleteNotification: (notificationId) => {
    return api.delete(`/payments/notifications/${notificationId}`);
  },

  // Eliminar todas las notificaciones (con filtros opcionales)
  deleteAllNotifications: (params = {}) => {
    return api.delete('/payments/notifications', { params });
  }

};


// API de health check

export const healthAPI = {

  checkHealth: () => {

    return axios.get(`${API_BASE_URL.replace('/api', '')}/health`);

  }

};


// API de autenticación
export const authAPI = {
  // Registro de usuario
  register: (data) => {
    return api.post('/auth/register', data);
  },

  // Verificar email
  verifyEmail: (data) => {
    return api.post('/auth/verify-email', data);
  },

  // Login
  login: (data) => {
    return api.post('/auth/login', data);
  },

  // Logout
  logout: () => {
    return api.post('/auth/logout');
  },

  // Forgot password
  forgotPassword: (data) => {
    return api.post('/auth/forgot-password', data);
  },

  // Reset password
  resetPassword: (data) => {
    return api.post('/auth/reset-password', data);
  },

  // Verificar token
  verifyToken: () => {
    return api.get('/auth/verify-token');
  },

  // Actualizar perfil
  updateProfile: (data) => {
    return api.put('/auth/profile', data);
  },

  // Cambiar contraseña
  changePassword: (data) => {
    return api.post('/auth/change-password', data);
  }
};

// API de usuarios
export const usersAPI = {
  // Perfil del usuario actual
  getProfile: () => {
    return api.get('/users/profile');
  },

  // Actualizar perfil
  updateProfile: (data) => {
    return api.put('/users/profile', data);
  },

  // Admin: Obtener todos los usuarios
  getUsers: (params = {}) => {
    return api.get('/users', { params });
  },

  // Admin: Obtener usuario por ID
  getUserById: (id) => {
    return api.get(`/users/${id}`);
  },

  // Admin: Crear usuario
  createUser: (data) => {
    return api.post('/users', data);
  },

  // Admin: Actualizar usuario
  updateUser: (id, data) => {
    return api.put(`/users/${id}`, data);
  },

  // Admin: Eliminar usuario
  deleteUser: (id) => {
    return api.delete(`/users/${id}`);
  },

  // Admin: Reenviar email de verificación
  resendVerification: (id) => {
    return api.post(`/users/${id}/resend-verification`);
  },

  // Admin: Suspender/Activar usuario
  toggleUserStatus: (id, data) => {
    return api.patch(`/users/${id}/toggle-status`, data);
  },

  // Admin: Obtener log de actividad del sistema
  getActivityLog: (params = {}) => {
    return api.get('/users/system/activity-log', { params });
  },

  // Admin: Descargar log de actividad en Excel
  downloadActivityLog: (params = {}) => {
    return api.get('/users/system/activity-log/download', { 
      params,
      responseType: 'blob'
    });
  }
};

export default api;

// API de archivos

export const filesAPI = {

  // Obtener lista de archivos

  getFiles: (publisherId, billerId) => {

    if (publisherId && billerId) {

      return api.get('/files', {

        headers: {

          'X-Publisher-ID': publisherId,

          'X-Biller-ID': billerId

        }

      });

    }

    return api.get('/files');

  },


  // Subir archivo

  uploadFile: (formData, publisherId, billerId) => {

    const headers = {

      'Content-Type': 'multipart/form-data'

    };



    if (publisherId && billerId) {

      headers['X-Publisher-ID'] = publisherId;

      headers['X-Biller-ID'] = billerId;

    }



    return api.post('/files/upload', formData, { headers });

  },


  // Descargar archivo

  downloadFile: (fileId, publisherId, billerId) => {

    const config = {

      responseType: 'blob'

    };



    if (publisherId && billerId) {

      config.headers = {

        'X-Publisher-ID': publisherId,

        'X-Biller-ID': billerId

      };

    }



    return api.get(`/files/${fileId}/download`, config);

  },


  // Renombrar archivo

  renameFile: (fileId, data, publisherId, billerId) => {

    if (publisherId && billerId) {

      return api.put(`/files/${fileId}/rename`, data, {

        headers: {

          'X-Publisher-ID': publisherId,

          'X-Biller-ID': billerId

        }

      });

    }

    return api.put(`/files/${fileId}/rename`, data);

  },


  // Eliminar archivo

  deleteFile: (fileId, publisherId, billerId) => {

    if (publisherId && billerId) {

      return api.delete(`/files/${fileId}`, {

        headers: {

          'X-Publisher-ID': publisherId,

          'X-Biller-ID': billerId

        }

      });

    }

    return api.delete(`/files/${fileId}`);

  },


  // Obtener historial de archivo

  getFileHistory: (fileId, publisherId, billerId) => {

    if (publisherId && billerId) {

      return api.get(`/files/${fileId}/history`, {

        headers: {

          'X-Publisher-ID': publisherId,

          'X-Biller-ID': billerId

        }

      });

    }

    return api.get(`/files/${fileId}/history`);

  },


  // Obtener información de archivo específico

  getFile: (fileId, publisherId, billerId) => {

    if (publisherId && billerId) {

      return api.get(`/files/${fileId}`, {

        headers: {

          'X-Publisher-ID': publisherId,

          'X-Biller-ID': billerId

        }

      });

    }

    return api.get(`/files/${fileId}`);

  }

};

// API de templates
export const templatesAPI = {

  // Obtener todos los templates
  getAll: (params = {}) => {
    const config = {};

    // Si hay publisher_id y biller_id, enviarlos como headers para contexto de tenant
    if (params.publisher_id && params.biller_id) {
      config.headers = {
        'X-Publisher-ID': params.publisher_id,
        'X-Biller-ID': params.biller_id
      };
      // Remover de params para no duplicar en query string
      const { publisher_id, biller_id, ...restParams } = params;
      const queryString = new URLSearchParams(restParams).toString();
      return api.get(`/templates${queryString ? `?${queryString}` : ''}`, config);
    }

    const queryString = new URLSearchParams(params).toString();
    return api.get(`/templates${queryString ? `?${queryString}` : ''}`);
  },

  // Obtener template por ID
  getById: (templateId) => {
    return api.get(`/templates/${templateId}`);
  },

  // Obtener templates por biller
  getByBiller: (billerId) => {
    return api.get(`/templates/biller/${billerId}`);
  },

  // Obtener template específico por biller, canal y tipo
  getTemplate: (billerId, channel, templateType) => {
    const params = new URLSearchParams();
    if (billerId) params.append('biller_id', billerId);
    params.append('channel', channel);
    params.append('template_type', templateType);
    
    return api.get(`/templates/search?${params.toString()}`);
  },

  // Crear template
  create: (templateData) => {
    return api.post('/templates', templateData);
  },

  // Actualizar template
  update: (templateId, templateData) => {
    return api.put(`/templates/${templateId}`, templateData);
  },

  // Eliminar template
  delete: (templateId, options = {}) => {
    const config = {};
    if (options.publisherId && options.billerId) {
      config.headers = {
        'X-Publisher-ID': options.publisherId,
        'X-Biller-ID': options.billerId
      };
    }
    return api.delete(`/templates/${templateId}`, config);
  },

  // Obtener templates default
  getDefaults: () => {
    return api.get('/templates/defaults');
  },

  // Preview template con datos de ejemplo
  preview: (templateData, sampleData = {}) => {
    return api.post('/templates/preview', {
      template: templateData,
      sample_data: sampleData
    });
  },

  // Enviar email de prueba
  sendTest: (templateData, testEmail, sampleData = {}) => {
    return api.post('/templates/send-test', {
      template: templateData,
      test_email: testEmail,
      sample_data: sampleData
    });
  },

  // Obtener tipos de template disponibles
  getTypes: () => {
    return api.get('/templates/types');
  },

  // Obtener historial de cambios de un template
  getHistory: (templateId) => {
    return api.get(`/templates/${templateId}/history`);
  },

  // Obtener una versión específica del historial
  getHistoryVersion: (templateId, version) => {
    return api.get(`/templates/${templateId}/history/${version}`);
  }
};

// API de tipos de template
export const templateTypesAPI = {
  
  // Obtener todos los tipos de template
  getAll: (params = {}) => {
    return api.get('/template-types', { params });
  },

  // Obtener tipo de template por ID
  getById: (typeId) => {
    return api.get(`/template-types/${typeId}`);
  },

  // Obtener tipos activos (para dropdowns)
  getActive: (params = {}) => {
    return api.get('/template-types/active', { params });
  },

  // Obtener tipos para un biller específico (con fallback)
  getForBiller: (billerId) => {
    return api.get(`/template-types/biller/${billerId}`);
  },

  // Crear nuevo tipo de template
  create: (typeData) => {
    return api.post('/template-types', typeData);
  },

  // Actualizar tipo de template
  update: (typeId, typeData) => {
    return api.put(`/template-types/${typeId}`, typeData);
  },

  // Eliminar tipo de template
  delete: (typeId) => {
    return api.delete(`/template-types/${typeId}`);
  }
};

// API de vectores de notificación
export const notificationVectorsAPI = {
  
  // === Funciones administrativas ===

  // Obtener todos los vectores de notificación (admin)
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/notification-vectors/admin?${queryString}` : '/notification-vectors/admin';
    return api.get(url);
  },

  // Obtener vector por ID (admin)
  getById: (vectorId) => {
    return api.get(`/notification-vectors/admin/${vectorId}`);
  },

  // Crear vector (admin)
  create: (vectorData) => {
    return api.post('/notification-vectors/admin', vectorData);
  },

  // Actualizar vector (admin)
  update: (vectorId, vectorData) => {
    return api.put(`/notification-vectors/admin/${vectorId}`, vectorData);
  },

  // Eliminar vector (admin)
  delete: (vectorId) => {
    return api.delete(`/notification-vectors/admin/${vectorId}`);
  },

  // === Funciones para usuarios biller ===

  // Obtener todos los vectores (biller context)
  getAllBiller: (params = {}, publisherId, billerId) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/notification-vectors?${queryString}` : '/notification-vectors';
    return api.get(url, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener vector por ID (biller context)
  getByIdBiller: (vectorId, publisherId, billerId) => {
    return api.get(`/notification-vectors/${vectorId}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Crear vector (biller context)
  createBiller: (vectorData, publisherId, billerId) => {
    return api.post('/notification-vectors', vectorData, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Actualizar vector (biller context)
  updateBiller: (vectorId, vectorData, publisherId, billerId) => {
    return api.put(`/notification-vectors/${vectorId}`, vectorData, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Eliminar vector (biller context)
  deleteBiller: (vectorId, publisherId, billerId) => {
    return api.delete(`/notification-vectors/${vectorId}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener opciones para dropdowns
  getOptions: () => {
    return api.get('/notification-vectors/admin/options/all');
  },

  // Obtener billers de un publisher específico
  getBillersByPublisher: (publisherId) => {
    return api.get(`/notification-vectors/admin/billers-by-publisher?publisher_id=${publisherId}`);
  },

  // === Funciones para API externa ===

  // Obtener vectores disponibles para asignar
  getAvailable: () => {
    return api.get('/notification-vectors/available');
  },

  // === Funciones VIEW-ONLY (no requieren admin, solo permisos) ===

  // Obtener todos los vectores (view-only para usuarios no admin)
  getAllViewOnly: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/notification-vectors?${queryString}` : '/notification-vectors';
    return api.get(url);
  },

  // Obtener opciones para dropdowns (view-only)
  getOptionsViewOnly: () => {
    return api.get('/notification-vectors/options');
  },

  // Obtener vector por ID (view-only)
  getByIdViewOnly: (vectorId) => {
    return api.get(`/notification-vectors/${vectorId}`);
  }
};

// API de configuraciones de billers
export const billerConfigurationsAPI = {
  
  // === Funciones administrativas ===
  
  // Obtener vectores de notificación disponibles
  getAvailableVectors: () => {
    return api.get('/biller-configurations/admin/available-vectors');
  },
  
  // Obtener todas las configuraciones de billers
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/biller-configurations/admin?${queryString}` : '/biller-configurations/admin';
    return api.get(url);
  },

  // Obtener configuración específica
  getByBiller: (publisherSystemId, billerId) => {
    return api.get(`/biller-configurations/admin/${publisherSystemId}/${billerId}`);
  },

  // Crear configuración
  create: (configData) => {
    return api.post('/biller-configurations/admin', configData);
  },

  // Actualizar configuración
  update: (publisherSystemId, billerId, configData) => {
    return api.put(`/biller-configurations/admin/${publisherSystemId}/${billerId}`, configData);
  },

  // Eliminar configuración
  delete: (publisherSystemId, billerId) => {
    return api.delete(`/biller-configurations/admin/${publisherSystemId}/${billerId}`);
  },

  // === Funciones para API externa ===
  
  // Obtener configuración de notificaciones para un biller
  getNotificationConfig: (publisherSystemId, billerId) => {
    return api.get(`/biller-configurations/${publisherSystemId}/${billerId}/notification-config`);
  },

  // Crear configuración desde API externa
  createExternal: (configData) => {
    return api.post('/biller-configurations/external', configData);
  },

  // Actualizar desde API externa
  updateExternal: (publisherSystemId, billerId, configData) => {
    return api.put(`/biller-configurations/${publisherSystemId}/${billerId}/external`, configData);
  }
};

// API de billers
export const billersAPI = {
  
  // === Funciones para templates (originales) ===
  
  // Obtener todos los billers (lista simple para templates)
  getList: () => {
    return api.get('/billers/list');
  },

  // === Funciones administrativas (nueva funcionalidad) ===
  
  // Obtener vectores de notificación disponibles
  getAvailableVectors: () => {
    return api.get('/billers/admin/available-vectors');
  },
  
  // Obtener todos los billers con paginación y filtros (admin)
  getAll: (queryString = '') => {
    const url = queryString ? `/billers/admin?${queryString}` : '/billers/admin';
    return api.get(url);
  },

  // Obtener billers de un sistema específico
  getBySystem: (systemId) => {
    return api.get(`/billers/admin?publisher_system_id=${systemId}`);
  },

  // Obtener biller por ID
  getById: (billerId) => {
    return api.get(`/billers/${billerId}`);
  },

  // Crear biller manualmente (admin)
  create: (billerData) => {
    return api.post('/billers/admin', billerData);
  },

  // Actualizar biller (admin)
  update: (billerId, billerData) => {
    return api.put(`/billers/admin/${billerId}`, billerData);
  },

  // Eliminar biller (admin)
  delete: (billerId) => {
    return api.delete(`/billers/admin/${billerId}`);
  },

  // Eliminar biller con confirmación (admin)
  deleteWithConfirmation: (billerId, confirmationData) => {
    return api.delete(`/billers/admin/${billerId}`, { data: confirmationData });
  },

  // Obtener lotes disponibles para análisis
  getAvailableBatches: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/batch-summary`);
  },

  // Obtener análisis de medios de pago
  getPaymentMethodsAnalysis: (billerId, batches, showAmounts = false) => {
    const batchesParam = Array.isArray(batches) ? batches.join(',') : batches;
    return api.get(`/billers/${billerId}/dashboard/payment-methods-analysis?batches=${encodeURIComponent(batchesParam)}&showAmounts=${showAmounts}`);
  },

  // Obtener indicadores de cobranza (KPIs)
  getBillerCollectionKPIs: (billerId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/dashboard/collection-kpis${queryString ? `?${queryString}` : ''}`);
  },

  // Obtener score de cobranza por cliente
  getBillerCustomerScores: (billerId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/dashboard/customer-scores${queryString ? `?${queryString}` : ''}`);
  },

  // === Funciones de Collection Score Plan ===

  // Obtener scores de cobranza con filtros y paginación
  getCollectionScores: (billerId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores${queryString ? `?${queryString}` : ''}`);
  },

  // Calcular scores de cobranza
  calculateCollectionScores: (billerId, data = {}) => {
    return api.post(`/billers/${billerId}/collection-scores/calculate`, data);
  },

  // Obtener analytics de scores
  getCollectionScoreAnalytics: (billerId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/analytics${queryString ? `?${queryString}` : ''}`);
  },

  // Obtener configuraciones de score
  getCollectionScoreConfigs: (billerId) => {
    return api.get(`/billers/${billerId}/collection-scores/configs`);
  },

  // Crear configuración de score
  createCollectionScoreConfig: (billerId, config) => {
    return api.post(`/billers/${billerId}/collection-scores/configs`, config);
  },

  // Actualizar configuración de score
  updateCollectionScoreConfig: (billerId, configId, config) => {
    return api.put(`/billers/${billerId}/collection-scores/configs/${configId}`, config);
  },

  // Obtener score específico de un cliente
  getCustomerCollectionScore: (billerId, customerId) => {
    return api.get(`/billers/${billerId}/collection-scores/${customerId}`);
  },

  // Crear plan de cobranza personalizado
  createCollectionPlan: (billerId, customerId, plan) => {
    return api.post(`/billers/${billerId}/collection-scores/${customerId}/plan`, plan);
  },

  // === COLLECTION ALERTS & DASHBOARDS ===

  // Obtener alertas activas del biller
  getActiveAlerts: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Dashboard de alertas
  getAlertsDashboard: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/dashboard/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Dashboard de recomendaciones
  getRecommendationsDashboard: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/dashboard/recommendations${queryParams ? '?' + queryParams : ''}`);
  },

  // Resolver una alerta
  resolveAlert: (billerId, alertId, resolvedBy, resolvedNote = null) => {
    return api.put(`/billers/${billerId}/collection-scores/alerts/${alertId}/resolve`, {
      resolvedBy,
      resolvedNote
    });
  },

  // === ADVANCED CONFIGURATION ===

  // Obtener configuración avanzada
  getAdvancedConfig: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/config/advanced${queryParams ? '?' + queryParams : ''}`);
  },

  // Actualizar configuración avanzada
  updateAdvancedConfig: (billerId, configType, configData, publisherId = null) => {
    return api.put(`/billers/${billerId}/collection-scores/config/advanced/${configType}`, {
      publisherId,
      configData
    });
  },

  // Obtener análisis avanzado detallado de un cliente
  getAdvancedAnalysis: (billerId, customerEmail) => {
    // URL encode del email para manejar caracteres especiales
    const encodedEmail = encodeURIComponent(customerEmail);
    return api.get(`/billers/${billerId}/collection-scores/${encodedEmail}/advanced`);
  },

  // === INDUSTRY MANAGEMENT ===

  // Obtener todas las industrias
  getIndustries: () => {
    return api.get('/billers/admin/industries');
  },

  // Obtener billers con industrias asignadas
  getBillersWithIndustries: () => {
    return api.get('/billers/admin/with-industries');
  },

  // Actualizar industria de un biller
  updateBillerIndustry: (billerId, data) => {
    return api.put(`/billers/admin/${billerId}/industry`, data);
  },

  // Asignación masiva automática de industrias
  bulkAssignIndustries: () => {
    return api.post('/billers/admin/industries/bulk-assign');
  }
};

// API de sistemas externos
export const publisherSystemsAPI = {
  
  // Obtener todos los sistemas externos
  getAll: () => {
    return api.get('/publisher-systems');
  },

  // Obtener sistema externo por ID
  getById: (systemId) => {
    return api.get(`/publisher-systems/${systemId}`);
  },

  // Crear nuevo sistema externo
  create: (systemData) => {
    return api.post('/publisher-systems', systemData);
  },

  // Actualizar sistema externo
  update: (systemId, systemData) => {
    return api.put(`/publisher-systems/${systemId}`, systemData);
  },

  // Eliminar sistema externo
  delete: (systemId) => {
    return api.delete(`/publisher-systems/${systemId}`);
  },

  // Regenerar API key
  regenerateApiKey: (systemId) => {
    return api.post(`/publisher-systems/${systemId}/regenerate-key`);
  },

  // Obtener billers de un publisher específico
  getBillers: (publisherId) => {
    return api.get(`/publisher-systems/${publisherId}/billers`);
  },

  // Subir logo
  uploadLogo: (publisherId, formData, config) => {
    return api.post(`/publisher-systems/${publisherId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    });
  },

  // Eliminar logo
  deleteLogo: (publisherId) => {
    return api.delete(`/publisher-systems/${publisherId}/logo`);
  }
};

// API de depuración de facturas
export const purgeAPI = {
  
  // Obtener estadísticas de depuración
  getStats: () => {
    return api.get('/purge/stats');
  },

  // Buscar facturas depuradas
  searchPurged: (filters) => {
    return api.get('/purge/search', { params: filters });
  },

  // Obtener candidatos para depuración
  getCandidates: (limit = 100) => {
    return api.get('/purge/candidates', { params: { limit } });
  },

  // Depurar factura específica
  purgePaymentReminder: (paymentId, reason) => {
    return api.post('/purge/payment-reminder', { payment_id: paymentId, reason });
  },

  // Depurar múltiples facturas
  purgeBatch: (paymentIds, reason) => {
    return api.post('/purge/batch', { payment_ids: paymentIds, reason });
  },

  // Ejecutar depuración automática manualmente
  runAutomaticPurge: (batchSize = 1000) => {
    return api.post('/purge/run-automatic', { batch_size: batchSize });
  }
};

// API de dashboard de biller
export const billerDashboardAPI = {
  
  // Obtener información general del biller
  getInfo: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/info`);
  },

  // Obtener estadísticas de facturas
  getStats: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/stats`);
  },

  // Obtener estadísticas de notificaciones
  getNotificationStats: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/notifications`);
  },
  // Obtener resumen por canal con emails leídos
  getChannelSummary: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/channel-summary`);
  },
  // Obtener resumen por lote
  getBatchSummary: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/batch-summary`);
  },

  // Obtener distribución de medios de pago por día
  getPaymentMethodsDistribution: (billerId, batch) => {
    return api.get(`/billers/${billerId}/dashboard/payment-methods?batch=${encodeURIComponent(batch)}`);
  },

  // Obtener análisis de medios de pago
  getPaymentMethodsAnalysis: (billerId, batches, showAmounts = false) => {
    const batchesParam = Array.isArray(batches) ? batches.join(',') : batches;
    return api.get(`/billers/${billerId}/dashboard/payment-methods-analysis?batches=${encodeURIComponent(batchesParam)}&showAmounts=${showAmounts}`);
  },

  // Obtener evolución histórica de medios de pago (snapshots mensuales)
  getPaymentMethodsEvolution: (billerId, startMonth, endMonth, mode = 'amount') => {
    return api.get(`/billers/${billerId}/payment-methods/evolution?startMonth=${startMonth}&endMonth=${endMonth}&mode=${mode}`);
  },

  // Obtener evolución de facturación mensual por cuenta
  getBillingEvolution: (billerId, params = {}) => {
    const {
      months = 12,
      page = 1,
      limit = 10,
      search = '',
      sortField = 'account_number',
      sortDirection = 'asc'
    } = params;

    const queryParams = new URLSearchParams({
      months: months.toString(),
      page: page.toString(),
      limit: limit.toString(),
      sortField,
      sortDirection
    });

    if (search && search.trim() !== '') {
      queryParams.append('search', search.trim());
    }

    return api.get(`/billers/${encodeURIComponent(billerId)}/dashboard/billing-evolution?${queryParams.toString()}`);
  }
};

// API de canales
export const channelsAPI = {

  // Obtener todos los canales
  getAll: () => {
    return api.get('/channels');
  },

  // Obtener solo canales habilitados/configurados
  getEnabled: () => {
    return api.get('/channels/enabled');
  },

  // === GESTIÓN DE COLA DE EMAILS ===

  // Obtener estadísticas de la cola
  getEmailQueueStats: (publisherId, billerId) => {
    return api.get('/channels/email/queue/stats', {
      params: { publisher_id: publisherId, biller_id: billerId }
    });
  },

  // Obtener emails en cola
  getEmailQueue: (publisherId, billerId, params = {}) => {
    return api.get('/channels/email/queue', {
      params: { publisher_id: publisherId, biller_id: billerId, ...params }
    });
  },

  // Vaciar cola de emails
  clearEmailQueue: (publisherId, billerId, status = null) => {
    return api.delete('/channels/email/queue', {
      data: { publisher_id: publisherId, biller_id: billerId, status }
    });
  },

  // Reintentar emails fallidos
  retryFailedEmails: (publisherId, billerId) => {
    return api.post('/channels/email/queue/retry-failed', {
      publisher_id: publisherId,
      biller_id: billerId
    });
  }
};

// API de precios
export const pricesAPI = {
  
  // Obtener todas las listas de precios
  getAll: (params = {}) => {
    return api.get('/prices', { params });
  },

  // Obtener lista de precios por ID
  getById: (priceListId) => {
    return api.get(`/prices/${priceListId}`);
  },

  // Crear nueva lista de precios
  create: (priceListData) => {
    return api.post('/prices', priceListData);
  },

  // Actualizar lista de precios
  update: (priceListId, priceListData) => {
    return api.put(`/prices/${priceListId}`, priceListData);
  },

  // Eliminar lista de precios
  delete: (priceListId) => {
    return api.delete(`/prices/${priceListId}`);
  }
};

// API de logs
export const logsAPI = {
  
  // Obtener archivos de logs disponibles
  getFiles: () => {
    return api.get('/logs/files');
  },

  // Obtener contenido de un log específico
  getLogContent: (logType, params = {}) => {
    return api.get(`/logs/${logType}`, { params });
  },

  // Limpiar archivo de log (admin only)
  clearLog: (logType) => {
    return api.delete(`/logs/${logType}`);
  }
};

// API de administración
export const adminAPI = {
  
  // Inicializar sistema (eliminar todos los datos)
  initializeSystem: () => {
    return api.post('/admin/initialize-system');
  },

  // Obtener estadísticas del sistema
  getSystemStats: () => {
    return api.get('/admin/system-stats');
  },

  // Listar todos los backups disponibles
  listBackups: () => {
    return api.get('/admin/backups');
  },

  // Restaurar desde un backup específico
  restoreFromBackup: (backupId) => {
    return api.post(`/admin/backups/${backupId}/restore`);
  },

  // Eliminar un backup específico
  deleteBackup: (backupId) => {
    return api.delete(`/admin/backups/${backupId}`);
  },

  // Crear backup manual
  createManualBackup: () => {
    return api.post('/admin/create-backup');
  },

  // Obtener configuración de parámetros de depuración
  getPurgeConfig: () => {
    return api.get('/admin/purge-config');
  },

  // Actualizar configuración de parámetros de depuración
  updatePurgeConfig: (configData) => {
    return api.put('/admin/purge-config', configData);
  },

  // Ejecutar simulador de datos
  runSimulator: (simulatorParams) => {
    return api.post('/admin/run-simulator', simulatorParams);
  },

  // Ejecutar simulador de clientes
  simulateCustomers: (customerSimulatorParams) => {
    return api.post('/admin/simulate-customers', customerSimulatorParams);
  },

  // Obtener vectores de notificación disponibles
  getAvailableVectors: () => {
    return api.get('/admin/available-vectors');
  },

  // Obtener billers disponibles
  getAvailableBillers: () => {
    return api.get('/admin/available-billers');
  },

  // Exportar dashboard a PDF
  exportDashboardToPDF: (billerId, options = {}) => {
    return api.post(`/billers/${billerId}/dashboard/export-pdf`, options);
  },

  // Exportar reporte ejecutivo a PDF
  exportExecutiveDashboardToPDF: (billerId, options = {}) => {
    return api.post(`/billers/${billerId}/dashboard/export-executive-pdf`, options);
  },

  // Obtener información de todos los tenants (desde caché)
  getTenants: () => {
    return api.get('/admin/tenants');
  },

  // Refrescar caché de todos los tenants
  refreshAllTenants: () => {
    return api.post('/admin/tenants/refresh-all');
  },

  // Refrescar caché de un tenant específico
  refreshSingleTenant: (tenantKey) => {
    return api.post(`/admin/tenants/${tenantKey}/refresh`);
  },

  // Obtener información de las bases de datos del sistema (Central y Model)
  getSystemDatabases: () => {
    return api.get('/admin/system-databases');
  }
};

// === API AVANZADA DE COLLECTION SCORES ===
export const advancedCollectionAPI = {


  // Análisis avanzado completo de un cliente
  getAdvancedAnalysis: (billerId, customerEmail, publisherId = null) => {
    const params = publisherId ? `?publisherId=${publisherId}` : '';
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/advanced${params}`);
  },

  // Obtener solo componentes de análisis
  getAnalysisComponents: (billerId, customerEmail, publisherId = null) => {
    const params = publisherId ? `?publisherId=${publisherId}` : '';
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/analysis${params}`);
  },

  // Obtener predicciones para un cliente
  getCustomerPredictions: (billerId, customerEmail, publisherId = null) => {
    const params = publisherId ? `?publisherId=${publisherId}` : '';
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/predictions${params}`);
  },

  // Obtener score avanzado existente
  getExistingAdvancedScore: (billerId, customerEmail, publisherId = null) => {
    const params = publisherId ? `?publisherId=${publisherId}` : '';
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/score-advanced${params}`);
  },

  // Obtener evolución de scores
  getScoreEvolution: (billerId, customerEmail, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/evolution${queryParams ? '?' + queryParams : ''}`);
  },

  // Obtener alertas activas del biller
  getActiveAlerts: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Obtener alertas de un cliente específico
  getCustomerAlerts: (billerId, customerEmail, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Resolver una alerta
  resolveAlert: (billerId, alertId, resolvedBy, resolvedNote = null) => {
    return api.put(`/billers/${billerId}/collection-scores/alerts/${alertId}/resolve`, {
      resolvedBy,
      resolvedNote
    });
  },

  // Dashboard de alertas
  getAlertsDashboard: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/dashboard/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Dashboard de recomendaciones
  getRecommendationsDashboard: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/dashboard/recommendations${queryParams ? '?' + queryParams : ''}`);
  },

  // Recálculo masivo de scores avanzados
  recalculateAdvancedScores: (billerId, options = {}) => {
    return api.post(`/billers/${billerId}/collection-scores/recalculate-advanced`, options);
  },

  // Health check del sistema avanzado
  checkAdvancedSystemHealth: (billerId) => {
    return api.get(`/billers/${billerId}/collection-scores/health-check`);
  },

  // Testing endpoint para análisis
  testAdvancedAnalysis: (billerId, customerEmail, publisherId = null) => {
    const params = publisherId ? `?publisherId=${publisherId}` : '';
    return api.get(`/billers/${billerId}/collection-scores/test-analysis/${customerEmail}${params}`);
  }
};

// API de gestión de clientes
export const customersAPI = {

  // Listar todos los clientes con filtros
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/customers${queryParams ? '?' + queryParams : ''}`);
  },

  // Obtener cliente específico
  getById: (id) => {
    return api.get(`/customers/${id}`);
  },

  // Obtener clientes por biller específico
  getByBiller: (publisherSystemId, billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/customers/by-biller/${publisherSystemId}/${billerId}${queryParams ? '?' + queryParams : ''}`;
    console.log('🌐 Calling API:', url, 'with params:', params);
    return api.get(url);
  },

  // Crear nuevo cliente
  create: (customerData) => {
    return api.post('/customers', customerData);
  },

  // Actualizar cliente
  update: (id, customerData) => {
    return api.put(`/customers/${id}`, customerData);
  },

  // Eliminar cliente
  delete: (id) => {
    return api.delete(`/customers/${id}`);
  },

  // Eliminar todos los clientes de un publisher-biller
  massDelete: (publisherSystemId, billerId) => {
    console.log('🗑️ Mass deleting customers for:', { publisherSystemId, billerId });
    return api.delete(`/customers/mass-delete/${publisherSystemId}/${billerId}`);
  },

  // Obtener historial de pagos de un cliente por customer_id numérico
  getPaymentHistory: (customerId) => {
    return api.get(`/customers/${customerId}/payment-history`);
  },

  // Obtener historial de pagos por account_id (payer_id)
  getPaymentHistoryByAccount: (accountId) => {
    return api.get(`/customers/by-account/${accountId}/payment-history`);
  },

  // Obtener historial completo de pagos por email (incluye pagos completos y parciales)
  getCustomerPaymentHistoryByEmail: (customerEmail, billerId, publisherId = null) => {
    const url = `/payments/billers/${billerId}/customer/${encodeURIComponent(customerEmail)}/history`;
    return publisherId ? api.get(`${url}?publisherId=${publisherId}`) : api.get(url);
  },

  // Función auxiliar para obtener publisher_system_biller_id
  getPublisherSystemBillerId: async (publisherSystemId, billerId) => {
    // Para AYSA, devolver directamente el ID conocido
    if (billerId === 'AYSA' || billerId === 'Aysa') {
      return { publisher_system_biller_id: 10 };
    }

    try {
      const response = await api.get(`/billers/${billerId}/publisher-systems`);
      const publisherBiller = response.data.publisher_systems.find(
        ps => ps.publisher_system_id.toString() === publisherSystemId.toString()
      );

      if (!publisherBiller) {
        throw new Error(`Publisher System ${publisherSystemId} not found for Biller ${billerId}`);
      }

      return { publisher_system_biller_id: publisherBiller.id };
    } catch (error) {
      // Fallback: buscar directamente en la base de datos con los parámetros conocidos
      console.warn('Using fallback for getPublisherSystemBillerId', error);
      return { publisher_system_biller_id: 10 }; // ID conocido para AYSA
    }
  }
};

// API de schedulers (Admin)
export const schedulersAPI = {

  // Obtener información de todos los schedulers
  getSchedulers: () => {
    return api.get('/admin/schedulers');
  },

  // Ejecutar scheduler manualmente (con timeout extendido para procesos largos)
  executeScheduler: (schedulerId) => {
    // Timeout de 5 minutos (300 segundos) para schedulers de larga duración
    // como el de proyecciones que puede tardar hasta 3 minutos
    return api.post(`/admin/schedulers/${schedulerId}/execute`, {}, {
      timeout: 300000 // 5 minutos
    });
  },

  // Obtener logs de schedulers
  getLogs: (lines = 50, filter = '') => {
    const params = { lines };
    if (filter) params.filter = filter;
    return api.get('/admin/schedulers/logs', { params });
  },

  // Obtener configuración específica de un scheduler
  getSchedulerById: (schedulerId) => {
    return api.get(`/admin/schedulers/${schedulerId}`);
  },

  // Actualizar configuración de scheduler
  updateSchedulerConfig: (schedulerId, config) => {
    return api.put(`/admin/schedulers/${schedulerId}/config`, config);
  },

  // Habilitar/deshabilitar scheduler
  toggleScheduler: (schedulerId, enabled) => {
    return api.post(`/admin/schedulers/${schedulerId}/toggle`, { enabled });
  },

  // Recargar configuración de scheduler desde BD (aplica cambios sin reiniciar backend)
  reloadScheduler: (schedulerId) => {
    return api.post(`/admin/schedulers/${schedulerId}/reload`);
  },

  // Eliminar todos los scores de cobranza existentes
  clearAllScores: () => {
    return api.post('/admin/schedulers/daily_score_recalculation/clear-scores');
  }
};

// API de Renegociación de Deuda
export const negotiationAPI = {

  // === GESTIÓN DE PLANES ===

  // Obtener todos los planes con filtros
  getPlans: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/negotiation/plans${queryParams ? '?' + queryParams : ''}`);
  },

  // Obtener plan por ID
  getPlanById: (planId, publisherId, billerId) => {
    return api.get(`/negotiation/plans/${planId}`, {
      params: { publisher_id: publisherId, biller_id: billerId }
    });
  },

  // Crear nuevo plan
  createPlan: (planData) => {
    return api.post('/negotiation/plans', planData);
  },

  // Actualizar plan existente
  updatePlan: (planId, planData) => {
    return api.put(`/negotiation/plans/${planId}`, planData);
  },

  // Desactivar plan
  deactivatePlan: (planId, publisherId, billerId) => {
    return api.delete(`/negotiation/plans/${planId}`, {
      data: { publisher_id: publisherId, biller_id: billerId }
    });
  },

  // Validar elegibilidad de cliente para un plan
  validateCustomerEligibility: (planId, eligibilityData) => {
    return api.post(`/negotiation/plans/${planId}/validate-eligibility`, eligibilityData);
  },

  // === GESTIÓN DE CAMPAÑAS ===

  // Obtener todas las campañas con filtros
  getCampaigns: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/negotiation/campaigns${queryParams ? '?' + queryParams : ''}`);
  },

  // Obtener campaña por ID
  getCampaignById: (campaignId, publisherId, billerId) => {
    return api.get(`/negotiation/campaigns/${campaignId}`, {
      params: { publisher_id: publisherId, biller_id: billerId }
    });
  },

  // Crear nueva campaña
  createCampaign: (campaignData) => {
    return api.post('/negotiation/campaigns', campaignData);
  },

  // Obtener métricas de una campaña
  getCampaignMetrics: (campaignId, publisherId, billerId) => {
    return api.get(`/negotiation/campaigns/${campaignId}/metrics`, {
      params: { publisher_id: publisherId, biller_id: billerId }
    });
  },

  // Obtener clientes de una campaña con matriz de pagos
  getCampaignCustomers: (campaignId, publisherId, billerId, filters = {}) => {
    const params = {
      publisher_id: publisherId,
      biller_id: billerId,
      ...filters
    };
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/negotiation/campaigns/${campaignId}/customers?${queryParams}`);
  },

  // Actualizar estado de una campaña
  updateCampaignStatus: (campaignId, statusData) => {
    return api.put(`/negotiation/campaigns/${campaignId}/status`, statusData);
  },

  // Enviar notificaciones de campaña
  sendCampaignNotifications: (campaignId, notificationData) => {
    return api.post(`/negotiation/campaigns/${campaignId}/send-notifications`, notificationData);
  },

  // === SELECCIÓN DE CANDIDATOS ===

  // Seleccionar candidatos desde alertas de cobranza
  selectCandidatesFromAlerts: (criteria) => {
    return api.post('/negotiation/candidates/from-alerts', criteria);
  },

  // === ENDPOINTS PÚBLICOS (sin autenticación) ===

  // Obtener datos de aceptación de plan (público - por token)
  getPublicAcceptanceData: (acceptanceToken, publisherId, billerId) => {
    return api.get(`/negotiation/public/acceptance/${acceptanceToken}`, {
      params: { publisher_id: publisherId, biller_id: billerId }
    });
  },

  // Procesar aceptación de plan (público - por token)
  processPublicAcceptance: (acceptanceToken, acceptanceData) => {
    return api.post(`/negotiation/public/acceptance/${acceptanceToken}`, acceptanceData);
  }
};

// API de Inflación (Configuración de proyecciones mensuales)
export const inflationAPI = {

  // Obtener años disponibles con datos de inflación
  getAvailableYears: (billerId, publisherId) => {
    return api.get(`/billers/${billerId}/inflation/years`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener datos de un año específico
  getYearData: (billerId, year, publisherId) => {
    return api.get(`/billers/${billerId}/inflation/${year}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Guardar/actualizar datos de un año completo
  saveYearData: (billerId, year, monthlyData, publisherId) => {
    return api.post(`/billers/${billerId}/inflation/${year}`, { monthlyData }, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Eliminar datos de un año
  deleteYear: (billerId, year, publisherId) => {
    return api.delete(`/billers/${billerId}/inflation/${year}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener datos de inflación para un período (para gráficos)
  getPeriodData: (billerId, startYear, startMonth, endYear, endMonth, publisherId) => {
    return api.get(`/billers/${billerId}/inflation/period`, {
      params: { startYear, startMonth, endYear, endMonth },
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  }
};

// API de configuración de IA
export const aiAPI = {
  // Credenciales
  getCredentials: () => api.get('/admin/ai-credentials'),
  getCredentialByProvider: (provider) => api.get(`/admin/ai-credentials/${provider}`),
  upsertCredential: (data) => api.post('/admin/ai-credentials', data),
  testConnection: (provider) => api.post(`/admin/ai-credentials/${provider}/test`),
  deleteCredential: (provider) => api.delete(`/admin/ai-credentials/${provider}`),

  // Prompts
  getPrompts: () => api.get('/admin/ai-prompts'),
  getPromptById: (id) => api.get(`/admin/ai-prompts/${id}`),
  getActivePrompt: (reportType) => api.get(`/admin/ai-prompts/active/${reportType}`),
  createPrompt: (data) => api.post('/admin/ai-prompts', data),
  updatePrompt: (id, data) => api.put(`/admin/ai-prompts/${id}`, data),
  deletePrompt: (id) => api.delete(`/admin/ai-prompts/${id}`),
  activatePrompt: (id) => api.post(`/admin/ai-prompts/${id}/activate`),
  getProviders: () => api.get('/admin/ai-prompts/providers')
};

// Exportar la instancia de api para uso directo
export { api };

